import { Link, routes } from "wasp/client/router";
import { Card, CardContent } from "../../client/components/ui/card";
import { CardSectionHeader } from "../../client/components/ui/card-section-header";
import { Button } from "../../client/components/ui/button";
import { Plus, RotateCcw } from "lucide-react";
import { useDashboardData } from "./DashboardDataContext";

export default function RoutinesWidget() {
  const { routines, pendingRoutines, handleCompleteRoutine, isDark, readOnly, t } = useDashboardData();
  return (
    <Card className="h-full">
      <CardSectionHeader
        label={t.dashboard.todayRoutines}
        isDark={isDark}
        compact
        rightContent={
          <Link to={routes.TradingRoutinesRoute.to}>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">{t.dashboard.viewAll}</Button>
          </Link>
        }
      />
      <CardContent className="px-4 pb-3 pt-1">
        {(routines?.length ?? 0) === 0 ? (
          <div className="flex flex-col items-center justify-center py-2 gap-1.5 text-center">
            <RotateCcw className="h-5 w-5 text-muted-foreground/40" />
            <p className="text-xs text-muted-foreground leading-tight">{t.dashboard.noActiveRoutinesDesc}</p>
            {!readOnly && (
              <Link to={routes.TradingRoutinesRoute.to}>
                <Button size="sm" variant="default" className="text-xs h-6 px-2">
                  <Plus className="h-3 w-3 mr-1" />
                  {t.dashboard.createFirstRoutine}
                </Button>
              </Link>
            )}
          </div>
        ) : pendingRoutines.length === 0 ? (
          <div className="flex items-center gap-2 text-xs text-primary py-1">
            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            {t.dashboard.allRoutinesDone}
          </div>
        ) : (
          <div className="space-y-1.5">
            {pendingRoutines.slice(0, 3).map((r: any) => (
              <div key={r.id} className="flex items-center justify-between gap-1.5">
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                  <span className="text-xs truncate">{r.name}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`text-[9px] font-semibold px-1 py-0.5 rounded-full ${
                    r.frequency === "daily"
                      ? "bg-primary/10 text-primary"
                      : r.frequency === "weekly"
                      ? "bg-blue-500/10 text-blue-400"
                      : "bg-purple-500/10 text-purple-400"
                  }`}>
                    {r.frequency === "daily" ? t.dashboard.daily : r.frequency === "weekly" ? t.dashboard.weekly : t.dashboard.monthly}
                  </span>
                  {!readOnly && (
                    <button
                      onClick={() => handleCompleteRoutine(r)}
                      className="w-4 h-4 rounded-full border-2 border-muted-foreground/30 hover:border-primary hover:bg-primary/10 transition-colors flex items-center justify-center group"
                      title={t.dashboard.markRoutineComplete}
                    >
                      <svg className="w-2 h-2 text-transparent group-hover:text-primary transition-colors" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
            {pendingRoutines.length > 3 && (
              <p className="text-[10px] text-muted-foreground pt-0.5">+{pendingRoutines.length - 3} {t.dashboard.activeRoutines.toLowerCase()}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
